/**
 * Footer Section Component
 *
 * Features a modern, comprehensive footer with navigation links,
 * social media integration, trust statements, and community engagement.
 * Organized in a 3-column layout with modern design elements.
 *
 * @example
 * <Footer />
 */
import React, { useRef, useState, useEffect, FormEvent } from 'react';
import { motion, useInView } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { LocaleLink } from '@lib/i18n/exports';
import { useLocaleNavigate } from '@lib/i18n/useLocaleNavigate';
import {
  Heart,
  Shield,
  Users,
  Globe,
  Mail,
  Phone,
  MapPin,
  ArrowRight,
  CheckCircle,
  Lock,
  Award,
  TrendingUp,
  Loader2,
  AlertCircle
} from 'lucide-react';
import { useAuth } from '../../../src/hooks/useAuth';
import { useRequireAuth } from '@/auth/useRequireAuth';
import { subscribeNewsletter } from '../../../features/newsletter/services/newsletterApi';
import { newsletterSubscribeSchema } from '../../../features/newsletter/validation/newsletterValidation';
import type { NewsletterError } from '../../../features/newsletter/types/newsletter.types';
import { isHalalDirectoryEnabled } from '../../../features/listings/featureFlag';

// TypeScript interfaces for type safety
interface SocialLink {
  id: string;
  platform: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
}

interface SocialLinkProps {
  social: SocialLink;
  className?: string;
}

// Social media links
const socialLinks: SocialLink[] = [
  {
    id: 'facebook',
    platform: 'Facebook',
    href: 'https://facebook.com/amalq',
    icon: Users,
    color: 'blue'
  },
  {
    id: 'twitter',
    platform: 'Twitter',
    href: 'https://twitter.com/amalq',
    icon: TrendingUp,
    color: 'sky'
  },
  {
    id: 'instagram',
    platform: 'Instagram',
    href: 'https://instagram.com/amalq',
    icon: Heart,
    color: 'rose'
  },
  {
    id: 'linkedin',
    platform: 'LinkedIn',
    href: 'https://linkedin.com/company/amalq',
    icon: Globe,
    color: 'blue'
  }
];

// Trust badges and compliance information
const trustBadges = [
  { id: 'ssl', textKey: 'footer.trust.ssl', icon: Lock, color: 'emerald' },
  { id: 'pci', textKey: 'footer.trust.pci', icon: Shield, color: 'blue' },
  {
    id: 'gdpr',
    textKey: 'footer.trust.gdpr',
    icon: CheckCircle,
    color: 'purple'
  },
  { id: 'iso', textKey: 'footer.trust.iso', icon: Award, color: 'amber' }
];

// Social Link Component
const SocialLink: React.FC<SocialLinkProps> = ({ social, className }) => {
  const getColorClasses = (color: string) => {
    const colorMap: Record<string, string> = {
      blue: 'bg-blue-600 hover:bg-blue-700',
      sky: 'bg-sky-500 hover:bg-sky-600',
      rose: 'bg-rose-500 hover:bg-rose-600',
      emerald: 'bg-emerald-600 hover:bg-emerald-700'
    };
    return colorMap[color] || 'bg-gray-600 hover:bg-gray-700';
  };

  const socialVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.4,
        ease: [0.25, 0.46, 0.45, 0.94] as const
      }
    }
  };

  return (
    <motion.a
      href={social.href}
      target="_blank"
      rel="noopener noreferrer"
      className={`h-10 w-10 rounded-full ${getColorClasses(social.color)} flex transform items-center justify-center text-white transition-all duration-300 hover:scale-110 hover:shadow-lg ${className}`}
      variants={socialVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '-50px' }}
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.95 }}
    >
      <social.icon className="h-5 w-5" />
    </motion.a>
  );
};

// Trust Badge Component
const TrustBadge: React.FC<{
  badge: {
    id: string;
    textKey: string;
    icon: React.ComponentType<{ className?: string }>;
    color: string;
  };
}> = ({ badge }) => {
  const { t } = useTranslation();
  const getColorClasses = (color: string) => {
    const colorMap: Record<string, string> = {
      emerald: 'text-emerald-400',
      blue: 'text-blue-400',
      purple: 'text-purple-400',
      amber: 'text-amber-400'
    };
    return colorMap[color] || 'text-gray-400';
  };

  const badgeVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.5,
        ease: [0.25, 0.46, 0.45, 0.94] as const
      }
    }
  };

  return (
    <motion.div
      className="flex items-center gap-2 text-sm text-gray-400"
      variants={badgeVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '-50px' }}
    >
      <badge.icon className={`h-4 w-4 ${getColorClasses(badge.color)}`} />
      <span>{t(badge.textKey)}</span>
    </motion.div>
  );
};

// Main Footer Component
export const Footer: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const footerRef = useRef<HTMLElement>(null);
  const navigate = useLocaleNavigate();
  const { authState } = useAuth();
  const { requireAuth } = useRequireAuth();
  const { t } = useTranslation();

  // Newsletter subscription state
  const [email, setEmail] = useState('');
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [subscribeStatus, setSubscribeStatus] = useState<
    'idle' | 'success' | 'error'
  >('idle');
  const [subscribeMessage, setSubscribeMessage] = useState('');

  const isInView = useInView(footerRef, {
    once: true,
    margin: '-50px', // Reduced margin for better mobile detection
    amount: 0.1 // Lower threshold for mobile
  });

  useEffect(() => {
    if (isInView) {
      setIsVisible(true);
    } else {
      // Fallback for mobile devices where intersection observer might not work reliably
      const isMobile = window.innerWidth <= 768;
      if (isMobile) {
        // Delay visibility on mobile to ensure proper loading
        const timer = setTimeout(() => setIsVisible(true), 300);
        return () => clearTimeout(timer);
      }
    }
  }, [isInView]);

  /**
   * Handles Start Campaign navigation with auth-aware routing.
   * If user is logged in, navigates directly to create campaign.
   * If not logged in, navigates to login page.
   */
  const handleStartCampaignClick = (
    e: React.MouseEvent<HTMLAnchorElement>
  ): void => {
    e.preventDefault();

    if (authState.isAuthenticated && authState.user) {
      navigate('/dashboard/create-campaign');
      return;
    }

    requireAuth({
      reason: 'Sign in to start a campaign',
      returnTo: '/dashboard/create-campaign',
      onAuthenticated: () => navigate('/dashboard/create-campaign')
    });
  };

  /**
   * Handles newsletter subscription form submission.
   * Validates email using Zod schema, shows loading state, and provides user feedback.
   */
  const handleNewsletterSubmit = async (
    e: FormEvent<HTMLFormElement>
  ): Promise<void> => {
    e.preventDefault();

    // Reset previous status
    setSubscribeStatus('idle');
    setSubscribeMessage('');

    // Validate email using Zod schema
    const validationResult = newsletterSubscribeSchema.safeParse({ email });

    if (!validationResult.success) {
      setSubscribeStatus('error');
      setSubscribeMessage(t('footer.newsletter.errorInvalidEmail'));
      return;
    }

    setIsSubscribing(true);

    try {
      // Call newsletter subscription API
      const response = await subscribeNewsletter(validationResult.data.email);

      if (response.success) {
        setSubscribeStatus('success');
        setSubscribeMessage(t('footer.newsletter.success'));
        setEmail('');

        // Clear success message after 5 seconds
        setTimeout(() => {
          setSubscribeStatus('idle');
          setSubscribeMessage('');
        }, 5000);
      } else {
        throw new Error(
          response.message || 'Failed to subscribe to newsletter'
        );
      }
    } catch (error) {
      // Handle different error types with translated messages
      let errorMessage = t('footer.newsletter.errorDefault');

      if (error && typeof error === 'object' && 'code' in error) {
        const newsletterError = error as NewsletterError;

        switch (newsletterError.code) {
          case 'INVALID_EMAIL':
          case 'VALIDATION_ERROR':
            errorMessage = t('footer.newsletter.errorInvalidEmail');
            break;
          case 'ALREADY_SUBSCRIBED':
            errorMessage = t('footer.newsletter.errorAlreadySubscribed');
            break;
          case 'RATE_LIMIT_EXCEEDED':
            errorMessage = t('footer.newsletter.errorRateLimit');
            break;
          case 'NETWORK_ERROR':
            errorMessage = t('footer.newsletter.errorNetwork');
            break;
          case 'SERVER_ERROR':
            errorMessage = t('footer.newsletter.errorServer');
            break;
          default:
            errorMessage = newsletterError.message || errorMessage;
        }
      } else if (error instanceof Error) {
        if (
          error.message.includes('Failed to fetch') ||
          error.message.includes('NetworkError')
        ) {
          errorMessage = t('footer.newsletter.errorNetwork');
        } else if (error.message.includes('timeout')) {
          errorMessage = t('footer.newsletter.errorTimeout');
        } else {
          errorMessage = error.message;
        }
      }

      setSubscribeStatus('error');
      setSubscribeMessage(errorMessage);

      // Clear error message after 5 seconds
      setTimeout(() => {
        setSubscribeStatus('idle');
        setSubscribeMessage('');
      }, 5000);
    } finally {
      setIsSubscribing(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.2,
        ease: [0.25, 0.46, 0.45, 0.94] as const
      }
    }
  };

  const headerVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.25, 0.46, 0.45, 0.94] as const
      }
    }
  };

  return (
    <footer
      ref={footerRef}
      className="relative overflow-hidden bg-linear-to-br from-gray-900 via-gray-800 to-gray-900 text-white"
      aria-label="Footer"
    >
      {/* Enhanced Background Pattern */}
      <div className="absolute inset-0">
        <div className="absolute right-10 top-10 h-32 w-32 opacity-[0.03]">
          <svg
            viewBox="0 0 100 100"
            fill="currentColor"
            className="text-emerald-400"
          >
            <circle cx="50" cy="50" r="40" />
          </svg>
        </div>
        <div className="absolute bottom-20 left-16 h-24 w-24 opacity-[0.03]">
          <svg
            viewBox="0 0 100 100"
            fill="currentColor"
            className="text-teal-400"
          >
            <polygon points="50,10 90,90 10,90" />
          </svg>
        </div>
        <div className="absolute left-1/2 top-1/2 h-40 w-40 -translate-x-1/2 -translate-y-1/2 transform opacity-[0.02]">
          <svg
            viewBox="0 0 100 100"
            fill="currentColor"
            className="text-emerald-400"
          >
            <rect x="20" y="20" width="60" height="60" />
          </svg>
        </div>
      </div>

      {/* Wide container - 5% margins for consistency with header/sections */}
      <div className="relative z-10 mx-auto w-full px-4 sm:px-[5%]">
        {/* Enhanced Footer Header */}
        <motion.div
          className="border-b border-gray-700/50 py-16 text-center lg:py-20"
          variants={headerVariants}
          initial="hidden"
          animate={isVisible ? 'visible' : 'hidden'}
        >
          {/* Trust Statement Badge - navigates to About Us Core Identity section */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={
              isVisible ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.8 }
            }
            transition={{
              duration: 0.6,
              delay: 0.1,
              ease: [0.25, 0.46, 0.45, 0.94] as const
            }}
          >
            <LocaleLink
              to="/about-us#core-identity-heading"
              className="mb-8 inline-flex items-center justify-center rounded-full border border-emerald-700/50 bg-linear-to-r from-emerald-900/50 to-teal-900/50 px-6 py-3 backdrop-blur-sm focus:outline-hidden focus:ring-2 focus:ring-emerald-500/70 focus:ring-offset-2 focus:ring-offset-gray-900"
              aria-label={t('footer.ourFoundation')}
            >
              <Heart className="me-3 h-5 w-5 text-emerald-400" />
              <span className="text-sm font-medium text-emerald-300">
                {t('footer.ourFoundation')}
              </span>
            </LocaleLink>
          </motion.div>

          {/* Main Headline */}
          <motion.h2
            className="mb-6 font-display text-3xl font-bold leading-tight tracking-tight text-white lg:text-4xl xl:text-5xl"
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{
              duration: 0.8,
              delay: 0.2,
              ease: [0.25, 0.46, 0.45, 0.94] as const
            }}
          >
            {t('footer.headline')}
            <span className="block bg-linear-to-r from-emerald-400 to-teal-400 bg-clip-text py-1 leading-normal text-transparent">
              {t('footer.headlineHighlight')} (
              <bdi className="inline-block leading-relaxed">عمل</bdi>)
            </span>
          </motion.h2>

          {/* Trust Badges */}
          <motion.div
            className="flex flex-wrap items-center justify-center gap-6 lg:gap-8"
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{
              duration: 0.8,
              delay: 0.4,
              ease: [0.25, 0.46, 0.45, 0.94] as const
            }}
          >
            {trustBadges.map(badge => (
              <TrustBadge key={badge.id} badge={badge} />
            ))}
          </motion.div>
        </motion.div>

        {/* Main Footer Content */}
        <motion.div
          className="py-16 lg:py-20"
          variants={containerVariants}
          initial="hidden"
          animate={isVisible ? 'visible' : 'hidden'}
        >
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-16">
            {/* Platform Section */}
            <div>
              <h3 className="mb-4 font-display text-lg font-bold text-white">
                {t('footer.platform')}
              </h3>
              <ul className="space-y-3">
                <li>
                  <LocaleLink
                    to="/campaigns"
                    className="group flex items-center gap-2 text-gray-300 transition-colors duration-200 hover:text-white"
                  >
                    <span className="transition-transform duration-200 group-hover:translate-x-1 group-hover:rtl:-translate-x-1">
                      {t('footer.links.browseCampaigns')}
                    </span>
                  </LocaleLink>
                </li>
                {isHalalDirectoryEnabled() && (
                  <li>
                    <LocaleLink
                      to="/listings"
                      className="group flex items-center gap-2 text-gray-300 transition-colors duration-200 hover:text-white"
                    >
                      <span className="transition-transform duration-200 group-hover:translate-x-1 group-hover:rtl:-translate-x-1">
                        {t('nav.directory')}
                      </span>
                    </LocaleLink>
                  </li>
                )}
                <li>
                  <LocaleLink
                    to="/gazian"
                    className="group flex items-center gap-2 text-gray-300 transition-colors duration-200 hover:text-white"
                  >
                    <span className="transition-transform duration-200 group-hover:translate-x-1 group-hover:rtl:-translate-x-1">
                      {t('footer.links.gazianWater')}
                    </span>
                  </LocaleLink>
                </li>
                <li>
                  <LocaleLink
                    to="/gazian/water"
                    className="group flex items-center gap-2 text-gray-300 transition-colors duration-200 hover:text-white"
                  >
                    <span className="transition-transform duration-200 group-hover:translate-x-1 group-hover:rtl:-translate-x-1">
                      {t('footer.links.orderGazianWater')}
                    </span>
                  </LocaleLink>
                </li>
                <li>
                  <LocaleLink
                    to="/ashrafie"
                    className="group flex items-center gap-2 text-gray-300 transition-colors duration-200 hover:text-white"
                  >
                    <span className="transition-transform duration-200 group-hover:translate-x-1 group-hover:rtl:-translate-x-1">
                      {t('footer.links.ashrafie', { defaultValue: 'Ashrafie Rewards' })}
                    </span>
                  </LocaleLink>
                </li>
                <li>
                  <LocaleLink
                    to="/fundasister"
                    className="group flex items-center gap-2 transition-colors duration-200"
                  >
                    <span className="text-gray-300 transition-all duration-200 group-hover:translate-x-1 group-hover:text-white group-hover:rtl:-translate-x-1">
                      {t('nav.fundasister')}
                    </span>
                    <span className="rounded-full border border-emerald-500/40 bg-emerald-600/30 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-emerald-400">
                      {t('nav.newBadge')}
                    </span>
                  </LocaleLink>
                </li>
                <li>
                  <LocaleLink
                    to="/about-us#how-it-works"
                    className="group flex items-center gap-2 text-gray-300 transition-colors duration-200 hover:text-white"
                  >
                    <span className="transition-transform duration-200 group-hover:translate-x-1 group-hover:rtl:-translate-x-1">
                      {t('nav.howItWorks')}
                    </span>
                  </LocaleLink>
                </li>
                <li>
                  <LocaleLink
                    to="/dashboard/create-campaign"
                    onClick={handleStartCampaignClick}
                    className="group flex items-center gap-2 text-gray-300 transition-colors duration-200 hover:text-white"
                  >
                    <span className="transition-transform duration-200 group-hover:translate-x-1 group-hover:rtl:-translate-x-1">
                      {t('actions.startCampaign')}
                    </span>
                  </LocaleLink>
                </li>
                <li>
                  <LocaleLink
                    to="/about-us#impact-heading"
                    className="group flex items-center gap-2 text-gray-300 transition-colors duration-200 hover:text-white"
                  >
                    <span className="transition-transform duration-200 group-hover:translate-x-1 group-hover:rtl:-translate-x-1">
                      {t('footer.links.successStories')}
                    </span>
                  </LocaleLink>
                </li>
                <li>
                  <LocaleLink
                    to="/about-us"
                    className="group flex items-center gap-2 text-gray-300 transition-colors duration-200 hover:text-white"
                  >
                    <span className="transition-transform duration-200 group-hover:translate-x-1 group-hover:rtl:-translate-x-1">
                      {t('nav.aboutUs')}
                    </span>
                  </LocaleLink>
                </li>
                <li>
                  <LocaleLink
                    to="/contact"
                    className="group flex items-center gap-2 text-gray-300 transition-colors duration-200 hover:text-white"
                  >
                    <span className="transition-transform duration-200 group-hover:translate-x-1 group-hover:rtl:-translate-x-1">
                      {t('nav.contact')}
                    </span>
                  </LocaleLink>
                </li>
                <li>
                  <LocaleLink
                    to="/blog"
                    className="group flex items-center gap-2 text-gray-300 transition-colors duration-200 hover:text-white"
                  >
                    <span className="transition-transform duration-200 group-hover:translate-x-1 group-hover:rtl:-translate-x-1">
                      {t('nav.blog')}
                    </span>
                  </LocaleLink>
                </li>
                <li>
                  <LocaleLink
                    to="/help"
                    className="group flex items-center gap-2 text-gray-300 transition-colors duration-200 hover:text-white"
                  >
                    <span className="transition-transform duration-200 group-hover:translate-x-1 group-hover:rtl:-translate-x-1">
                      {t('nav.helpCenter')}
                    </span>
                  </LocaleLink>
                </li>
                <li>
                  <LocaleLink
                    to="/changelog"
                    className="group flex items-center gap-2 text-gray-300 transition-colors duration-200 hover:text-white"
                  >
                    <span className="transition-transform duration-200 group-hover:translate-x-1 group-hover:rtl:-translate-x-1">
                      {t('nav.whatsNew')}
                    </span>
                  </LocaleLink>
                </li>
              </ul>
            </div>

            {/* Support & Legal Section */}
            <div>
              <h3 className="mb-4 font-display text-lg font-bold text-white">
                {t('footer.supportLegal')}
              </h3>
              <ul className="space-y-3">
                <li>
                  <LocaleLink
                    to="/privacy"
                    className="group flex items-center gap-2 text-gray-300 transition-colors duration-200 hover:text-white"
                  >
                    <span className="transition-transform duration-200 group-hover:translate-x-1 group-hover:rtl:-translate-x-1">
                      {t('footer.links.privacyPolicy')}
                    </span>
                  </LocaleLink>
                </li>
                <li>
                  <LocaleLink
                    to="/terms"
                    className="group flex items-center gap-2 text-gray-300 transition-colors duration-200 hover:text-white"
                  >
                    <span className="transition-transform duration-200 group-hover:translate-x-1 group-hover:rtl:-translate-x-1">
                      {t('footer.links.termsOfService')}
                    </span>
                  </LocaleLink>
                </li>
                <li>
                  <LocaleLink
                    to="/cookies"
                    className="group flex items-center gap-2 text-gray-300 transition-colors duration-200 hover:text-white"
                  >
                    <span className="transition-transform duration-200 group-hover:translate-x-1 group-hover:rtl:-translate-x-1">
                      {t('footer.links.cookiePolicy')}
                    </span>
                  </LocaleLink>
                </li>
                <li>
                  <LocaleLink
                    to="/islamic-compliance"
                    className="group flex items-center gap-2 text-gray-300 transition-colors duration-200 hover:text-white"
                  >
                    <span className="transition-transform duration-200 group-hover:translate-x-1 group-hover:rtl:-translate-x-1">
                      {t('footer.links.islamicCompliance')}
                    </span>
                  </LocaleLink>
                </li>
                <li>
                  <LocaleLink
                    to="/refund-policy"
                    className="group flex items-center gap-2 text-gray-300 transition-colors duration-200 hover:text-white"
                  >
                    <span className="transition-transform duration-200 group-hover:translate-x-1 group-hover:rtl:-translate-x-1">
                      {t('footer.links.refundPolicy')}
                    </span>
                  </LocaleLink>
                </li>
                <li>
                  <LocaleLink
                    to="/community-guidelines"
                    className="group flex items-center gap-2 text-gray-300 transition-colors duration-200 hover:text-white"
                  >
                    <span className="transition-transform duration-200 group-hover:translate-x-1 group-hover:rtl:-translate-x-1">
                      {t('footer.links.communityGuidelines')}
                    </span>
                  </LocaleLink>
                </li>
                <li>
                  <LocaleLink
                    to="/campaign-terms"
                    className="group flex items-center gap-2 text-gray-300 transition-colors duration-200 hover:text-white"
                  >
                    <span className="transition-transform duration-200 group-hover:translate-x-1 group-hover:rtl:-translate-x-1">
                      {t('footer.links.campaignTerms')}
                    </span>
                  </LocaleLink>
                </li>
                <li>
                  <LocaleLink
                    to="/payment-terms"
                    className="group flex items-center gap-2 text-gray-300 transition-colors duration-200 hover:text-white"
                  >
                    <span className="transition-transform duration-200 group-hover:translate-x-1 group-hover:rtl:-translate-x-1">
                      {t('footer.links.paymentTerms')}
                    </span>
                  </LocaleLink>
                </li>
                <li>
                  <LocaleLink
                    to="/data-protection"
                    className="group flex items-center gap-2 text-gray-300 transition-colors duration-200 hover:text-white"
                  >
                    <span className="transition-transform duration-200 group-hover:translate-x-1 group-hover:rtl:-translate-x-1">
                      {t('footer.links.dataProtection')}
                    </span>
                  </LocaleLink>
                </li>
              </ul>
            </div>
          </div>
        </motion.div>

        {/* Newsletter & Social Section */}
        <motion.div
          className="border-t border-gray-700/50 py-12"
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{
            duration: 0.8,
            delay: 0.8,
            ease: [0.25, 0.46, 0.45, 0.94] as const
          }}
        >
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
            {/* Newsletter Signup */}
            <div>
              <h3 className="mb-4 font-display text-xl font-bold text-white lg:text-2xl">
                {t('footer.community.title')}
              </h3>
              <p className="mb-6 leading-relaxed text-gray-300">
                {t('footer.community.description')}
              </p>

              {/* Newsletter Form */}
              <form onSubmit={handleNewsletterSubmit} className="space-y-3">
                <div className="flex flex-col gap-3 sm:flex-row">
                  <input
                    type="email"
                    value={email}
                    onChange={(e): void => setEmail(e.target.value)}
                    placeholder={t('footer.newsletter.placeholder')}
                    disabled={isSubscribing}
                    className={`flex-1 rounded-xl border bg-gray-800/50 px-4 py-3 text-white placeholder-gray-400 transition-all duration-200 focus:border-emerald-500/50 focus:outline-hidden focus:ring-2 focus:ring-emerald-500/50 ${
                      subscribeStatus === 'error'
                        ? 'border-red-500/50 focus:border-red-500/50 focus:ring-red-500/50'
                        : 'border-gray-600/50'
                    } ${
                      subscribeStatus === 'success'
                        ? 'border-emerald-500/50'
                        : ''
                    } ${isSubscribing ? 'cursor-not-allowed opacity-75' : ''}`}
                    aria-label={t('footer.newsletter.ariaLabel')}
                    aria-invalid={subscribeStatus === 'error'}
                    aria-describedby={
                      subscribeStatus !== 'idle'
                        ? 'subscribe-message'
                        : undefined
                    }
                  />
                  <button
                    type="submit"
                    disabled={isSubscribing}
                    className={`flex transform items-center justify-center gap-2 rounded-xl bg-linear-to-r from-emerald-600 to-teal-600 px-6 py-3 font-semibold text-white shadow-lg transition-all duration-300 ${
                      isSubscribing
                        ? 'cursor-not-allowed opacity-75'
                        : 'hover:scale-105 hover:from-emerald-700 hover:to-teal-700 hover:shadow-xl'
                    }`}
                    aria-label={t('footer.newsletter.button')}
                  >
                    {isSubscribing ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>{t('footer.newsletter.subscribing')}</span>
                      </>
                    ) : (
                      <>
                        {t('footer.newsletter.button')}
                        <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1 group-hover:rtl:-translate-x-1" />
                      </>
                    )}
                  </button>
                </div>

                {/* Status Message */}
                {subscribeStatus !== 'idle' && subscribeMessage && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    id="subscribe-message"
                    className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm ${
                      subscribeStatus === 'success'
                        ? 'border border-emerald-500/30 bg-emerald-500/20 text-emerald-300'
                        : 'border border-red-500/30 bg-red-500/20 text-red-300'
                    }`}
                    role="alert"
                    aria-live="polite"
                  >
                    {subscribeStatus === 'error' ? (
                      <AlertCircle className="h-4 w-4 shrink-0" />
                    ) : (
                      <CheckCircle className="h-4 w-4 shrink-0" />
                    )}
                    <span>{subscribeMessage}</span>
                  </motion.div>
                )}
              </form>
            </div>

            {/* Social Media & Contact */}
            <div className="space-y-8">
              {/* Social Media Links */}
              <div>
                <h4 className="mb-4 font-display text-lg font-semibold text-white">
                  {t('footer.social.title')}
                </h4>
                <div className="flex items-center gap-4">
                  {socialLinks.map(social => (
                    <SocialLink key={social.id} social={social} />
                  ))}
                </div>
              </div>

              {/* Contact Information */}
              <div>
                <h4 className="mb-4 font-display text-lg font-semibold text-white">
                  {t('footer.contact.title')}
                </h4>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-gray-300">
                    <Mail className="h-4 w-4 shrink-0 text-emerald-400" />
                    <span>{t('footer.contact.email')}</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <Phone className="h-4 w-4 shrink-0 text-emerald-400" />
                    <span>{t('footer.contact.support')}</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <MapPin className="h-4 w-4 shrink-0 text-emerald-400" />
                    <span>{t('footer.contact.serving')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Bottom Footer */}
        <motion.div
          className="border-t border-gray-700/50 py-8"
          initial={{ opacity: 0 }}
          animate={isVisible ? { opacity: 1 } : { opacity: 0 }}
          transition={{
            duration: 0.8,
            delay: 1.0,
            ease: [0.25, 0.46, 0.45, 0.94] as const
          }}
        >
          <div className="flex flex-col items-center justify-between gap-6 lg:flex-row">
            {/* Copyright & Brand */}
            <div className="flex flex-col items-center gap-4 text-sm text-gray-400 sm:flex-row">
              <div className="flex items-center gap-2">
                <Heart className="h-4 w-4 text-emerald-400" />
                <span className="font-brand font-semibold text-white">
                  {t('brand.name')}
                </span>
              </div>
              <span>
                {t('footer.copyright', { year: new Date().getFullYear() })}
              </span>
            </div>

            {/* Legal Links - Required Pages */}
            <div className="flex flex-wrap items-center gap-6 text-sm text-gray-400">
              <LocaleLink
                to="/privacy"
                className="transition-colors duration-200 hover:text-white"
              >
                {t('footer.links.privacyPolicy')}
              </LocaleLink>
              <LocaleLink
                to="/terms"
                className="transition-colors duration-200 hover:text-white"
              >
                {t('footer.links.termsOfService')}
              </LocaleLink>
              <LocaleLink
                to="/cookies"
                className="transition-colors duration-200 hover:text-white"
              >
                {t('footer.links.cookiePolicy')}
              </LocaleLink>
              <LocaleLink
                to="/islamic-compliance"
                className="transition-colors duration-200 hover:text-white"
              >
                {t('footer.links.islamicCompliance')}
              </LocaleLink>
            </div>
          </div>
        </motion.div>
      </div>
    </footer>
  );
};
